/** Copyright (c) 2019 T.Rowe Price. All rights reserved. */
import './MailingAddresses.scss';

import { isEmpty } from 'lodash';
import React, { Fragment } from 'react';
import { bool, object } from 'prop-types';

import Content, { contentConverter } from 'services/Content';
import { formatMailingAddressLocation } from 'services/Formats';
import { ReactComponent as WarningCircle } from 'assets/icons/warning-circle.svg';
import WithUserPermissions from 'components/shared/WithUserPermissions/WithUserPermissions';
import { addressOnFileEnabled } from 'services/FeatureFlags';

const mailingAddressPostalError = contentConverter.makeHtml(
  Content.mailingAddressPostalError
);
const mailingAddressSecurityMessage = contentConverter.makeHtml(
  addressOnFileEnabled() ? Content.mailingAddressSecurityMessageV2 : Content.mailingAddressSecurityMessage
);

const AddressLine = ({ line, lineKey }) => (
  <Fragment key={lineKey}>{line}</Fragment>
);

const AddressLocation = ({ address }) => (
  <Fragment>{formatMailingAddressLocation(address)}</Fragment>
);

const AddressLinesWithPermissions = WithUserPermissions(AddressLine);
const AddressLocationWithPermissions = WithUserPermissions(AddressLocation);

const Addresses = ({ address, addressLabel }) =>
  !isEmpty(address) && (
    <div className="cmm-modal-content-subgroup cmm-mailing-address">
      <div className="cmm-modal-content-flex-container">
        <div className="cmm-modal-content-header-container">
          <div className="cmm-modal-content-header">{addressLabel}</div>
          <div className="cmm-modal-content-header-details">
            <div>
              {address.addressLines.map((line, lineKey) => (
                <AddressLinesWithPermissions
                  line={line}
                  lineKey={lineKey}
                  key={lineKey}
                  shouldMask
                />
              ))}
              <div>
                <AddressLocationWithPermissions address={address} shouldMask />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

Addresses.propTypes = {
  address: object
};

export const InvalidMailingAddresses = ({ address, addressLabel }) =>
  !isEmpty(address) && (
    <div className="cmm-modal-content-subgroup cmm-content-error">
      <Fragment>
        <div className="cmm-modal-content-flex-container">
          <div className="cmm-modal-content-header-container">
            <div className="cmm-modal-content-header">{addressLabel}</div>
            <div className="cmm-modal-content-header-details">
              <div className="cmm-modal-icon-container">
                <WarningCircle className="cmm-modal-content-error-warning-icon" />
              </div>
              <div>
                {address.addressLines.map((line, lineKey) => (
                  <AddressLinesWithPermissions
                    key={lineKey}
                    line={line}
                    lineKey={lineKey}
                    shouldMask
                  />
                ))}
                <div>
                  <AddressLocationWithPermissions
                    address={address}
                    shouldMask
                  />
                </div>
              </div>
            </div>
          </div>
          <div
            className="cmm-modal-content-message"
            dangerouslySetInnerHTML={{ __html: mailingAddressPostalError }}
          />
        </div>
        <div className="cmm-modal-content-flex-container">
          <div className="cmm-modal-icon-container">
            <WarningCircle className="cmm-modal-content-error-warning-icon" />
          </div>
          <div
            className="cmm-modal-content-security-message"
            dangerouslySetInnerHTML={{ __html: mailingAddressSecurityMessage }}
          />
        </div>
      </Fragment>
    </div>
  );

InvalidMailingAddresses.propTypes = {
  address: object
};

export const ResidentialAddress = ({ address }) => {
  return <Addresses address={address}
                    addressLabel="Residential/Legal Address" />
};

const MailingAddresses = ({ address, errors = {}, sameAsResidential = false }) => {
  const {
    hasVerificationInvalidMailing,
    hasVerificationInvalidMailingAndEmail,
    hasVerificationInvalidMailingAndNoEmail
  } = errors;
  const hasValidMailingAddress = !(
    hasVerificationInvalidMailing ||
    hasVerificationInvalidMailingAndEmail ||
    hasVerificationInvalidMailingAndNoEmail
  );

  return hasValidMailingAddress ? (
    <Addresses address={address}
               addressLabel={sameAsResidential ? "Residential/Legal Address & Mailing Address" : "Mailing Address"} />
  ) : (
    <InvalidMailingAddresses address={address}
                             addressLabel={sameAsResidential ? "Residential/Legal Address & Mailing Address" : "Mailing Address"} />
  )
};

MailingAddresses.propTypes = {
  errors: object,
  hasNonVerificationError: bool
};

export default MailingAddresses;
